//-- Copyright (c) 2021 TMT Insights Group, LLC
import { useContext, useState } from 'react';
import { ConfigContext } from '@/contextAPI/configContext';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { JobDetail, OrderJob } from '@/redux/slices/slice_types';
import { getData } from '@/redux/slices/dataTableSlice';
import { selectCanWriteJobs } from '@/redux/selectors/authSelectors';
import {
  selectCurrentTableBulkActions,
  selectCurrentTableBulkActionsByStatusStage,
  selectCurrentTableKey
} from '@/redux/selectors/dataTableSelectors';
import { displayBulkActionIdent } from '@/_utils/utils';
import OverflowMenu from '@/components/_common/OverflowMenu/OverflowMenu';
import OverflowMenuItem from '@/components/_common/OverflowMenu/OverflowMenuItem';
import PolarisIcon, { IconDictionary } from '@/components/_common/PolarisIcon';
import CancelJobDialog from '@/components/_common/CancelJobDialog';
import EntityActionDialog from '@/components/_common/EntityActionDialog';

type JobsOverflowMenuProps = {
  row: OrderJob | JobDetail;
};

const JobsOverflowMenu = ({ row }: JobsOverflowMenuProps) => {
  const { id, status, stage } = row;
  const url = `jobs/${id}`;
  const dispatch = useAppDispatch();
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [dialogAction, setDialogAction] = useState<string | null>(null);
  const currentTableKey = useAppSelector(selectCurrentTableKey);
  const canWriteJobs = useAppSelector(selectCanWriteJobs);
  const actions = useAppSelector(selectCurrentTableBulkActions);
  const actionsByStatusStage = useAppSelector(selectCurrentTableBulkActionsByStatusStage);
  const bulkActionIcons = useContext(ConfigContext)?.bulkActionIcons || {};
  const disableCancelJob = status === 'complete' || status === 'cancelled';

  const isActionValid = (action: string) =>
    action !== 'update' && (actionsByStatusStage[status]?.actions.includes(action) || actionsByStatusStage[status]?.stage[stage]?.length);

  return (
    <>
      {actions ? (
        <EntityActionDialog
          id={id}
          entity="job"
          action={dialogAction}
          isOpen={!!dialogAction}
          setIsOpen={setDialogAction}
          afterSubmit={() => dispatch(getData())}
        />
      ) : (
        <CancelJobDialog isOpen={isCancelDialogOpen} setIsOpen={setIsCancelDialogOpen} jobId={id} afterSubmit={() => dispatch(getData() as any)} />
      )}
      <OverflowMenu>
        <OverflowMenuItem onClick={() => window.open(`${window.origin}/${url}`, currentTableKey === 'titleDetailJobs' ? '_blank' : '_self')}>
          <PolarisIcon icon="job" height={16} />
          <span>View Job</span>
        </OverflowMenuItem>
        {actions ? (
          canWriteJobs &&
          actions &&
          Object.keys(actions).map((action) => {
            if (!isActionValid(action)) return;
            return (
              <OverflowMenuItem key={action} onClick={() => setDialogAction(action)}>
                <PolarisIcon
                  icon={
                    actions?.[action]?.icon ||
                    bulkActionIcons[action] ||
                    (IconDictionary[action] && typeof IconDictionary[action] === 'string' ? IconDictionary[action] : 'edit')
                  }
                />
                <span>{displayBulkActionIdent(action)}</span>
              </OverflowMenuItem>
            );
          })
        ) : (
          <OverflowMenuItem onClick={() => setIsCancelDialogOpen(true)} disabled={disableCancelJob}>
            <PolarisIcon icon="cancel" height={17} />
            <span>Cancel Job</span>
          </OverflowMenuItem>
        )}
      </OverflowMenu>
    </>
  );
};

export default JobsOverflowMenu;
